import { Sender, Think } from '@ant-design/x';
import type { SenderRef } from '@ant-design/x/es/sender';
import { App, Button, Flex, theme } from 'antd';
import clsx from 'clsx';
import { ArrowDown, Mic, MicOff } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentChatMessaging } from '../../hooks/chat/useDocumentChatMessaging/useDocumentChatMessaging';
import { useWebSocketConnectionStatus } from '../../hooks/useWebSocketConnectionStatus';
import { getSpeechRecognition } from '../../lib/chat/messageActions';
import { toChatRealtimePhase } from '../../utils/webSocketConnectionStatus';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import { isChatNearBottom } from './documentChatScroll';
import DocumentChatMessagesList from './DocumentChatMessagesList';

const MAX_INPUT_LENGTH = 4000;

interface Props {
  /** `fileName` drives the right-panel header (same pattern as FileDetail). */
  file: { id: number | string; fileName: string };
  containerClassName?: string;
}

/**
 * Main AI document chat component. UI shell + scroll; messaging via hook.
 */
export const AiDocumentChat: React.FC<Props> = ({ file, containerClassName }) => {
  const { message: antMessage } = App.useApp();
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const webSocketStatus = useWebSocketConnectionStatus();
  const realtimePhase = toChatRealtimePhase(webSocketStatus);
  const isRealtimeConnected = realtimePhase === 'connected';

  const [inputValue, setInputValue] = useState<string>('');
  const [isDictating, setIsDictating] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [stickToBottom, setStickToBottom] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const senderRef = useRef<SenderRef>(null);
  const recognitionRef = useRef<{ stop: () => void } | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const stickToBottomRef = useRef(true);

  const setInputClamped = useCallback((next: string) => {
    setInputValue(next.slice(0, MAX_INPUT_LENGTH));
  }, []);

  const stopDictation = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsDictating(false);
  }, []);

  const forceStickToBottom = useCallback(() => {
    stickToBottomRef.current = true;
    setStickToBottom(true);
  }, []);

  const {
    chatHistory,
    isLoading,
    isLoadingHistory,
    handleSend,
    handleCancelPending,
    handleRetry,
    handleRegenerate,
  } = useDocumentChatMessaging({
    fileId: file.id,
    isRealtimeConnected,
    realtimePhase,
    forceStickToBottom,
    setInputClamped,
    stopDictation,
  });

  useEffect(() => {
    setSpeechSupported(!!getSpeechRecognition());
  }, []);

  useEffect(() => {
    const textareaElement = senderRef.current?.inputElement;
    if (textareaElement instanceof HTMLTextAreaElement) {
      textareaElement.setAttribute('aria-label', t('a11y.messageInput'));
    }
  }, [t]);

  useEffect(() => () => stopDictation(), [stopDictation]);

  const toggleDictation = useCallback(() => {
    if (isLoading || !isRealtimeConnected) return;
    if (isDictating) {
      stopDictation();
      return;
    }
    const Ctor = getSpeechRecognition();
    if (!Ctor) {
      antMessage.warning(t('chat.speechUnsupported'));
      return;
    }
    const recognition = new Ctor();
    recognition.lang = i18n.language || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (ev) => {
      const transcript = Array.from(ev.results)
        .map((r) => r[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (!transcript) return;
      setInputClamped(inputValue.trim() ? `${inputValue.trim()} ${transcript}` : transcript);
    };
    recognition.onerror = () => {
      setIsDictating(false);
      recognitionRef.current = null;
    };
    recognition.onend = () => {
      setIsDictating(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    setIsDictating(true);
    try {
      recognition.start();
    } catch {
      setIsDictating(false);
      recognitionRef.current = null;
      antMessage.error(t('chat.dictationFailed'));
    }
  }, [
    antMessage,
    i18n.language,
    inputValue,
    isDictating,
    isLoading,
    isRealtimeConnected,
    setInputClamped,
    stopDictation,
    t,
  ]);

  /** Stick while near bottom; skip when user scrolled up (Bubble.List autoScroll spirit). */
  const scheduleScrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (!stickToBottomRef.current) return;
      const scroller = messagesScrollRef.current;
      if (scroller) {
        if (behavior === 'smooth') {
          scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
        } else {
          scroller.scrollTop = scroller.scrollHeight;
        }
        return;
      }
      messagesEndRef.current?.scrollIntoView({ behavior });
    });
  }, []);

  const syncStickToBottomFromScroll = useCallback(() => {
    const scroller = messagesScrollRef.current;
    if (!scroller) return;
    const next = isChatNearBottom(scroller);
    stickToBottomRef.current = next;
    setStickToBottom(next);
  }, []);

  const jumpToLatest = useCallback(() => {
    forceStickToBottom();
    const scroller = messagesScrollRef.current;
    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [forceStickToBottom]);

  useEffect(() => {
    if (isFirstLoad && chatHistory.length > 0) {
      forceStickToBottom();
      scheduleScrollToBottom('auto');
      setIsFirstLoad(false);
    } else {
      const hasLiveAssistant = chatHistory.some((m) => m.status === 'Pending' && !m.isError);
      scheduleScrollToBottom(hasLiveAssistant || isLoading ? 'auto' : 'smooth');
    }
  }, [chatHistory, forceStickToBottom, isFirstLoad, isLoading, scheduleScrollToBottom]);

  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      scheduleScrollToBottom('auto');
    });
    ro.observe(content);
    return () => {
      ro.disconnect();
    };
  }, [scheduleScrollToBottom]);

  useEffect(
    () => () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    },
    [],
  );

  const hasPending = chatHistory.some((m) => m.status === 'Pending' && !m.isError);
  const senderBusy = isLoading || hasPending;
  const composerDisabled = senderBusy || !isRealtimeConnected;
  const hasMessages = chatHistory.length > 0;

  return (
    <div className={clsx('relative flex h-full flex-col px-2', containerClassName)}>
      {/* Full-width rule under title — FileDetail gets the same via Tabs nav border. */}
      <div className="border-gray-medium shrink-0 border-b pb-2">
        <h2 className="max-w-72 truncate text-xl font-medium">
          <TruncatedTooltip>{file.fileName}</TruncatedTooltip>
        </h2>
      </div>
      <div
        ref={messagesScrollRef}
        className="relative min-h-0 flex-1 overflow-y-auto"
        onScroll={syncStickToBottomFromScroll}
      >
        <div ref={messagesContentRef} className="pt-2">
          {hasMessages ? (
            <DocumentChatMessagesList
              messages={chatHistory}
              senderBusy={senderBusy}
              autoScroll={false}
              onRetryError={(idx) => {
                void handleRetry(idx);
              }}
              onRegenerate={(idx) => {
                void handleRegenerate(idx);
              }}
              bottomRef={messagesEndRef}
            />
          ) : null}
          {isLoadingHistory && (
            <div className="flex h-[200px] items-center justify-center">
              <Think loading blink title={t('common.loading')}>
                {t('common.loadingDescription')}
              </Think>
            </div>
          )}
          {!hasMessages && !isLoading && !isLoadingHistory && (
            <div className="flex h-full items-center justify-center">
              <span className="text-gray-dark m-auto">{t('common.startNewConversation')}</span>
            </div>
          )}
        </div>
      </div>

      {!stickToBottom && hasMessages ? (
        <div className="pointer-events-none absolute right-4 bottom-36 z-10">
          <Button
            type="default"
            shape="circle"
            size="large"
            className="pointer-events-auto! shadow-md!"
            aria-label={t('chat.jumpToLatest')}
            icon={<ArrowDown className="h-4 w-4" strokeWidth={1} aria-hidden />}
            onClick={jumpToLatest}
          />
        </div>
      ) : null}

      <div className="mt-4 mb-1.5 shrink-0">
        <p
          role="status"
          className="mb-2 px-1 text-center text-xs leading-snug"
          style={{ color: token.colorTextTertiary }}
          data-testid={
            realtimePhase === 'connected'
              ? 'chat-disclaimer'
              : realtimePhase === 'connecting'
                ? 'chat-connecting-disclaimer'
                : 'chat-offline-disclaimer'
          }
        >
          {realtimePhase === 'connected'
            ? t('chat.disclaimer')
            : realtimePhase === 'connecting'
              ? t('chat.connectingDisclaimer')
              : t('chat.offlineDisclaimer')}
        </p>
        <Sender
          ref={senderRef}
          value={inputValue}
          onChange={setInputClamped}
          onSubmit={() => {
            if (inputValue.trim() && !composerDisabled) {
              const toSend = inputValue;
              setInputValue('');
              void handleSend(toSend);
            }
          }}
          onCancel={handleCancelPending}
          loading={senderBusy}
          disabled={composerDisabled}
          submitType="enter"
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder={
            realtimePhase === 'disconnected'
              ? t('chat.offlineHint')
              : realtimePhase === 'connecting'
                ? t('chat.connectingHint')
                : t('chat.placeholder')
          }
          suffix={false}
          footer={(_, { components }) => {
            const { SendButton, LoadingButton } = components;
            return (
              <Flex justify="flex-end" align="center" gap="small">
                <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
                  {inputValue.length}/{MAX_INPUT_LENGTH}
                </span>
                {speechSupported ? (
                  <Button
                    type="text"
                    aria-label={isDictating ? t('chat.stopDictation') : t('chat.dictate')}
                    aria-pressed={isDictating}
                    icon={
                      isDictating ? (
                        <MicOff className="h-4 w-4" strokeWidth={1} aria-hidden />
                      ) : (
                        <Mic className="h-4 w-4" strokeWidth={1} aria-hidden />
                      )
                    }
                    onClick={toggleDictation}
                    disabled={composerDisabled}
                    shape="circle"
                    className={isDictating ? 'text-red-500!' : undefined}
                  />
                ) : null}
                {senderBusy ? (
                  <LoadingButton aria-label={t('a11y.stopGenerating')} disabled={false} />
                ) : (
                  <SendButton aria-label={t('a11y.sendMessage')} disabled={composerDisabled} />
                )}
              </Flex>
            );
          }}
        />
      </div>
    </div>
  );
};

export default AiDocumentChat;
